import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFiles } from '@dev-streetbeat/chat';
import usePrevious from 'shared/hooks/usePrevious';
import './styles.css';

// Component imports
import { UserMenu } from 'shared/components/user/UserMenu';
import { MenuItemButton } from './MenuItemButton/MenuItemButton';

// Store imports
import useAppStatus from 'shared/store/appStatusStore';
import useThreadsStore from 'shared/store/threadsStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import colorsStore from 'shared/store/colorsStore';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';

// Icon imports
import { ThreadHistoryIcon } from 'shared/assets/svg/menu-icons/ThreadHistoryIcon';
import { PlusIcon } from 'shared/assets/svg/menu-icons/PlusIcon';
import { AttachmentsIcon } from 'shared/assets/svg/menu-icons/AttachmentsIcon';
import { ConstraintsIcon } from 'shared/assets/svg/menu-icons/ConstraintsIcon';

// Utilities and configs
import { createNewChat } from 'utils/chatUtils';

// Styles
import styles from './MenuList.module.scss';

interface MenuListProps {
  onClose?: () => void;
  isOpen?: boolean;
}

export const MenuList: React.FC<MenuListProps> = ({ onClose, isOpen = false }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const isTabletOrBelow = useTabletAndBelowMediaQuery();
  const { t } = useTranslation();

  const setAppStatus = useAppStatus((state) => state.setAppStatus);
  const fromHistory = useThreadsStore((state) => state.fromHistory);
  const appStatus = useAppStatus((state) => state.appStatus);

  const {
    setConstraintsMainMenuRedDot,
    constraintsMainMenuRedDot,
    attachmentsRedDot,
    setAttachmentsRedDot,

    userSentFirstMessage,
    setNewAttachmentsAdded,
    newConstraintsAdded,
    setNewConstraintsAdded,
  } = activityPanelStore();

  const navigate = useNavigate();
  const [showAttachmentBubble, setShowAttachmentBubble] = useState(false);
  const [showConstraintsBubble, setShowConstraintsBubble] = useState(false);

  const { files } = useFiles();
  const prevFiles = usePrevious(files);

  useEffect(() => {
    const fileListLengthChanged = prevFiles && files.length > prevFiles.length && appStatus !== 'attachments';
    if (!fromHistory && userSentFirstMessage && fileListLengthChanged) {
      setAttachmentsRedDot(true);
      setShowAttachmentBubble(true);
      const timeout = setTimeout(() => {
        setNewAttachmentsAdded(false);
        setShowAttachmentBubble(false);
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [files, fromHistory, userSentFirstMessage]);

  useEffect(() => {
    if (!fromHistory && userSentFirstMessage && newConstraintsAdded) {
      setShowConstraintsBubble(true);
      const timeout = setTimeout(() => {
        setNewConstraintsAdded(false);
        setShowConstraintsBubble(false);
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [fromHistory, userSentFirstMessage, newConstraintsAdded]);

  return (
    <aside
      className={classNames(
        styles.appMenuContainer,
        'flex h-full w-24 flex-col justify-between transition-all duration-300 ease-in-out',
      )}
      style={{
        backgroundColor: colorsConfig.white,
        borderRight: `1px solid ${colorsConfig.grayLight}`,
      }}
    >
      <div className={styles.menuItemsContainer}>
        <MenuItemButton
          item={{
            icon: PlusIcon,
            name: 'newChat',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={false}
          onClick={() => {
            createNewChat(navigate);
            if (isTabletOrBelow && onClose) {
              onClose();
            }
          }}
        />
        <MenuItemButton
          item={{
            icon: ThreadHistoryIcon,
            name: 'threadHistory',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'history'}
          onClick={() => {
            if (appStatus === 'history') {
              setAppStatus('idle');
            } else {
              setAppStatus('history');
            }
          }}
        />
        <div className="separator" style={{ backgroundColor: colorsConfig.GrayLight2 }} />
        <MenuItemButton
          id="attachments"
          item={{
            icon: AttachmentsIcon,
            name: 'attachments',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'attachments'}
          onClick={() => {
            if (appStatus === 'attachments') {
              setAppStatus('idle');
            } else {
              setShowAttachmentBubble(false);
              setAttachmentsRedDot(false);
              setAppStatus('attachments');
            }
          }}
          showRedDot={attachmentsRedDot && files && files.length > 0}
          bubbleText={t('menu.newAttachmentsAdded')}
          showBubble={showAttachmentBubble}
          handleHideBubble={() => setShowAttachmentBubble(false)}
        />

        {/* <MenuItemButton
          id="constraints"
          item={{
            icon: ConstraintsIcon,
            name: 'constraints',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'constraints'}
          onClick={() => {
            if (appStatus === 'constraints') {
              setAppStatus('idle');
            } else {
              setShowConstraintsBubble(false);
              setConstraintsMainMenuRedDot(false);
              setAppStatus('constraints');
            }
          }}
          isDisabled={false}
          showRedDot={constraintsMainMenuRedDot}
          bubbleText={t('menu.newConstraintsAdded')}
          showBubble={showConstraintsBubble}
          handleHideBubble={() => setShowConstraintsBubble(false)}
        /> */}
      </div>

      <div className={styles.menuUserItemContainer}>
        <UserMenu />
      </div>
    </aside>
  );
};
